import React, { useEffect } from 'react';
import { Heading1, spacing } from '~/theme';
import { useTranslation } from 'react-i18next';
import { Heading2 } from '~/theme/headings';
import { BoldText } from '~/theme/elements';
import { CALLOUT_TYPE } from '~/constants';
import ClockIcon from '~/assets/icons/ontario-icon-clock.svg';
import PayFeeFaq from './pay-fee-faq';
import Callout from '~/components/common/callout';
import { TextStyle } from './styles';
import { styled } from 'styled-components';
import {
    DECLINED_ORDER_STATUS,
    RESET_CHECK_AMP,
    RESET_CHECK_REINST,
    SET_ERRORS,
    SET_IS_LOADING,
    SET_PAYFINE_ATTEMPTS,
    SHOW_CANCEL_ORDER_SUCCSS_MESSAGE,
} from '~/store/actions';
import { useStateValue } from '~/store/store';
import PageAlert from '~/components/common/page-alerts';
import PageErrors from '~/components/page-errors';
import { mapFormToPageErrors } from '~/utils/helpers';
import { FormProvider, useForm } from 'react-hook-form';
import Checkbox from '~/components/form-elements/checkbox';
import Button from '~/components/form-elements/button';
import { ErrorContainer } from '~/components/licence-plate-renewal/styles';
import InlineError from '~/components/form-elements/inline-error';
import { StyledTextParagraph } from 'pages/address-change/styles';
import { useRouter } from 'next/router';
import { useAuditTracking } from '~/hooks/use-audit-tracking';
import { PAY_FINE } from '~/config/audit-events';

const CalloutContent = styled.div`
    display: flex;
    flex-wrap: wrap;

    svg {
        width: ${spacing.spacing5};
        height: ${spacing.spacing5};
        margin-right: ${spacing.spacing1};
    }
`;
const DurationSpan = styled.span`
    display: inline-flex;
    white-space: pre;
`;

function PayFee() {
    const { t } = useTranslation();
    const { state, dispatch } = useStateValue();
    const { auditEvent } = useAuditTracking();
    type FormData = { isPrivacyConsent: boolean };
    const data: FormData = { isPrivacyConsent: false };
    const methods = useForm<FormData>({
        shouldFocusError: false,
        reValidateMode: 'onChange',
        defaultValues: data,
    });
    const {
        formState: { errors },
    } = methods;

    const errorList = mapFormToPageErrors(errors);
    const nextPageUrl = `/pay-fine/drivers-licence`;
    const router = useRouter();

    useEffect(() => {
        dispatch({ type: SET_IS_LOADING, payload: { isLoading: false } });
    }, []);

    useEffect(() => {
        if (state.checkAmp && state.checkAmp.length > 0) {
            dispatch({ type: RESET_CHECK_AMP });
        }
        if (state.checkReinst && state.checkReinst.length > 0) {
            dispatch({ type: RESET_CHECK_REINST });
        }
    }, [state.checkAmp, state.checkReinst, dispatch]);

    const onSubmit = (formData: FormData) => {
        console.log(formData);
        auditEvent(PAY_FINE.NAVIGATE);
        dispatch({
            type: SHOW_CANCEL_ORDER_SUCCSS_MESSAGE,
            payload: { cancelOrderSuccessMessage: false },
        });
        dispatch({ type: DECLINED_ORDER_STATUS, payload: { declineOrderStatus: false } });
        dispatch({ type: SET_IS_LOADING, payload: { isLoading: true } });
        dispatch({ type: SET_ERRORS, payload: { errors: [] } });
        dispatch({ type: SET_PAYFINE_ATTEMPTS, payload: { payFineAttempts: 0 } });

        router.push(nextPageUrl);
    };

    useEffect(() => {
        if (errorList && !!errorList.length) {
            document.getElementById('errorsContainer')?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [errorList]);

    return (
        <>
            <ErrorContainer id="errorsContainer" role="alert">
                {errorList && <PageErrors errors={errorList} />}
            </ErrorContainer>
            {state.cancelOrderSuccessMessage && (
                <PageAlert
                    alertType="success"
                    heading={t('pay-fine:cancelOrder:successfullCancelMessage')}
                    role="alert"
                ></PageAlert>
            )}
            {state.declineOrderStatus && (
                <PageAlert alertType="error" heading={t('errors:errorCodes:CXPAC018')} role="alert">
                    {t('errors:errorCodes:DVO-E701')}
                </PageAlert>
            )}
            <Heading1>{t('pay-fine:pay-fee.heading')}</Heading1>
            <Callout calloutType={CALLOUT_TYPE.CALLOUT}>
                <CalloutContent>
                    <ClockIcon aria-hidden="true" focusable="false" />
                    {t('pay-fine:pay-fee.timeEstimate.info')}
                    <DurationSpan>
                        <BoldText>{t('pay-fine:pay-fee.timeEstimate.time')}</BoldText>
                        {t('pay-fine:pay-fee.timeEstimate.unit')}
                    </DurationSpan>
                </CalloutContent>
            </Callout>
            <TextStyle>
                <Heading2>{t('pay-fine:pay-fee.subHeading')}</Heading2>
            </TextStyle>
            <PayFeeFaq />
            <FormProvider {...methods}>
                <form onSubmit={methods.handleSubmit(onSubmit)}>
                    <StyledTextParagraph>
                        {!!errorList?.length && (
                            <InlineError id="confirmation-required-error">
                                {t('pay-fine:pay-fee.inlineerror')}
                            </InlineError>
                        )}

                        <Checkbox
                            id="isPrivacyConsent"
                            hintId="confirmation-required-error"
                            name="isPrivacyConsent"
                            text={t('pay-fine:pay-fee.end')}
                            registerOptions={{ required: { message: 'DVO-F018', value: true } }}
                            errors={!!errors.isPrivacyConsent}
                            errorTitle="DVO-F018"
                            className="checkBox"
                        />
                    </StyledTextParagraph>
                    <Button>{t('common:continue')} </Button>
                </form>
            </FormProvider>
        </>
    );
}

export default PayFee;
